import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiDownload, FiEdit, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import { BarLoader } from 'react-spinners';
import Swal from 'sweetalert2';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles

const ViewDoc = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/doc/${id}`,
          { withCredentials: true }
        );
        setDocument(response.data.data);
        setLoading(false);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          toast.error(err.response?.data?.error || 'Failed to load document');
          navigate('/admin/doc');
        }
      }
    };

    fetchDocument();
  }, [id, navigate]);

  const handleDownload = () => {
    window.open(`${process.env.REACT_APP_API_URL}/${document.file}`, '_blank');
  };

  const handleEdit = () => {
    navigate(`/admin/editdoc/${id}`);
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/doc/${id}`,
          { withCredentials: true }
        );
        toast.success('Document deleted successfully');
        navigate('/admin/doc');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to delete document');
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <BarLoader color="#4F46E5" size={50} />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-500">Document not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate('/admin/doc')}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FiArrowLeft className="mr-2" />
            Back to Documents
          </button>
          <div className="flex space-x-3">
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FiEdit className="mr-2" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <FiTrash2 className="mr-2" />
              Delete
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <div className="border-b border-gray-200 pb-4 mb-4">
              <h1 className="text-2xl font-bold text-gray-800">{document.title}</h1>
              <div className="flex items-center mt-2">
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                  {document.type}
                </span>
                <span className="ml-4 text-sm text-gray-500">
                  Created: {formatDate(document.createdAt)}
                </span>
                {document.updatedAt !== document.createdAt && (
                  <span className="ml-4 text-sm text-gray-500">
                    Updated: {formatDate(document.updatedAt)}
                  </span>
                )}
              </div>
            </div>

            {document.description && (
              <div className="mb-6">
                <h2 className="text-lg font-medium text-gray-700 mb-2">Description</h2>
                <div 
                  className="ql-editor text-gray-600" 
                  dangerouslySetInnerHTML={{ __html: document.description }} 
                />
              </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <h2 className="text-lg font-medium text-gray-700 mb-4">Document File</h2>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-gray-200">
                    <FiDownload className="text-gray-600 text-xl" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">
                      {document.file.split('/').pop()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {document.file.split('.').pop().toUpperCase()} File
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .ql-editor {
          font-size: 16px;
          line-height: 1.6;
          padding: 0;
        }
        .ql-editor p {
          margin-bottom: 1rem;
        }
        .ql-editor ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin-bottom: 1rem;
        }
        .ql-editor ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin-bottom: 1rem;
        }
        .ql-editor h1, .ql-editor h2, .ql-editor h3 {
          font-weight: bold;
          margin-bottom: 1rem;
        }
        .ql-editor h1 {
          font-size: 2em;
        }
        .ql-editor h2 {
          font-size: 1.5em;
        }
        .ql-editor h3 {
          font-size: 1.17em;
        }
        .ql-editor a {
          color: #2563eb;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default ViewDoc;